import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../redux/store';
import { markBadgesSeen } from '../redux/slices/badges';
import { useCelebration } from '../components/ui/organisms/Celebration';
import { useUserBadges } from './useUserBadges';

/** Only celebrate unlocks earned within this window; older unseen unlocks are marked seen silently. */
const FRESH_WINDOW_MS = 10 * 60 * 1000;

/**
 * Watches the current user's unlocked badges and presents a one-time centred
 * celebration the moment a badge is earned.
 *
 * "Already celebrated" is server-authoritative via each unlock's `seenByUser`
 * flag, so a badge is celebrated exactly once across all of a user's devices.
 * To avoid a confetti storm the first time this ships (existing unlocks predate
 * the flag), only unlocks earned within {@link FRESH_WINDOW_MS} are celebrated —
 * all other unseen unlocks are marked seen silently.
 *
 * The unlock state is refreshed on every route change and on tab focus so the
 * celebration appears the moment the user finishes a task and navigates back —
 * badges only ever unlock as a side effect of an action on another page. Mount
 * this once, app-wide, from a single location.
 */
export const useBadgeCelebration = (): void => {
	const { badges, isLoading } = useUserBadges({ refreshOnNavigate: true });
	const userId = useAppSelector((state) => state.user.currentUser?.id);
	const dispatch = useAppDispatch();
	const celebration = useCelebration();
	// Ids we've already acted on this session, to avoid re-dispatching while the
	// server round-trip settles.
	const handledRef = useRef<Set<string>>(new Set());

	useEffect(() => {
		if (isLoading || !userId || badges.length === 0) {
			return;
		}

		const unseen = badges.filter(
			(badge) => badge.unlocked && !badge.seenByUser && !handledRef.current.has(badge.id)
		);
		if (unseen.length === 0) {
			return;
		}

		unseen.forEach((badge) => handledRef.current.add(badge.id));

		const now = Date.now();
		const fresh = unseen.filter(
			(badge) => badge.unlockedAt && now - Date.parse(badge.unlockedAt) < FRESH_WINDOW_MS
		);

		if (fresh.length === 1) {
			celebration.celebrate({
				title: 'Badge unlocked',
				message: fresh[0].name,
				emoji: '🏅'
			});
		} else if (fresh.length > 1) {
			celebration.celebrate({
				title: 'Badges unlocked',
				message: `${fresh.length} new badges earned!`,
				emoji: '🏅'
			});
		}

		// Mark every unseen unlock (fresh or historical) as seen so it is never
		// replayed on this or another device.
		dispatch(markBadgesSeen({ badgeIds: unseen.map((badge) => badge.id) }));
	}, [badges, isLoading, userId, dispatch, celebration]);
};
